import React, { FC, useContext, useState } from 'react';
import styled, { css } from 'styled-components';
import { useForm, FormProvider, FieldValues } from 'react-hook-form';
import { Spacer } from '../Spacer';
import { Input } from '../forms/Input';
import { Typography } from '../Typography';
import { Panel, PanelHeadingMarginSizes } from '../Panel';
import { ThemeColorsKeys } from '../../styles/tokens/colors';
import { UiStateContext } from '../../context/UiStateContext';
import { Button } from '../Button';
import { LeaseExtensionQuote } from '../forms/LeaseExtensionQuote';
import { PanelGroup } from '../PanelGroup';
import { useDataLayerPush } from '../../hooks/useDataLayerPush';
import { ThemeFontsKeys } from '../../styles/tokens/typography';
import { currency } from '../../utils';

// Interfaces

type Views = 'calculator' | 'disclaimer' | 'results';

export interface LeaseExtensionCalculatorProps {
  defaultPropertyValue?: number;
  defaultYearsRemainingLease?: number;
  defaultCurrentRent?: number;
  border?: boolean;
  introCopy?: string;
  heading?: string;
}

// Styles

const StyledFormContainer = styled(Spacer)`
  ${({ theme: { space, media } }) => css`
    container-type: inline-size;
  `};
`;

const StyledInlineFields = styled.div`
  ${({ theme: { space, media } }) => css`
    display: grid;
    grid-template-columns: 1fr;
    gap: ${space.md};

    @container (min-width: 650px) {
      grid-template-columns: 1fr 1fr;
      gap: ${space.sm};
    }
  `};
`;

const StyledTotalBox = styled.div`
  ${({ theme: { space, colors } }) => css`
    border-radius: 3px;
    padding: ${space.md};
    background-color: ${colors.grey2.default};
  `};
`;

// CONST
interface PanelConfig {
  heading: string;
  headingMargin: PanelHeadingMarginSizes;
  backgroundColor: ThemeColorsKeys;
  headingVariant?: ThemeFontsKeys;
}
const PANEL_CONFIG: PanelConfig = {
  heading: 'Lease extension calculator',
  headingMargin: 'double',
  backgroundColor: 'white',
};

interface LeaseExtensionCalculatorFormData {
  propertyValue: number;
  yearsRemainingLease: number;
  currentRent: number;
  nonUkResident: boolean;
  effectiveDate: string;
}

// JSX

export const LeaseExtensionCalculator: FC<LeaseExtensionCalculatorProps> = ({
  defaultPropertyValue,
  defaultYearsRemainingLease,
  defaultCurrentRent,
  border,
  introCopy,
  heading,
  ...rest
}) => {
  const [activeView, setActiveView] = useState<Views>('calculator');
  const [estimateLeaseCost, setEstimateLeaseCost] = useState<string>();
  const { setActiveBlade } = useContext(UiStateContext);
  const { dataLayerPush } = useDataLayerPush();

  const handleOpenBlade = () => {
    setActiveBlade('leaseExtensionQuote');
  };

  const formMethods = useForm<LeaseExtensionCalculatorFormData>({
    mode: 'onSubmit',
    defaultValues: {
      propertyValue: defaultPropertyValue,
      yearsRemainingLease: defaultYearsRemainingLease,
      currentRent: defaultCurrentRent,
    },
  });
  const { register, handleSubmit, watch, formState } = formMethods;
  const { errors, dirtyFields, touchedFields } = formState;

  // Watch fields
  const watchPropertyValue = watch('propertyValue');
  const watchYearsRemainingLease = watch('yearsRemainingLease');
  const watchCurrentRent = watch('currentRent');

  // Function to calculate the mortage repayments and interest and progress to next state
  const onSubmit = (formData) => {
    const { propertyValue, yearsRemainingLease, currentRent } = formData; // Demo values 400000, 87, 200

    // Data layer event
    dataLayerPush({
      event: 'leaseExtensionCalculator',
      lecFormStep: 'calculate lease extension',
      lecPropertyValue: propertyValue,
      lecYearsRemainingOnLease: yearsRemainingLease,
      lecCurrentRent: currentRent.toString(),
      formValidation: 'success',
      formError: undefined,
    });

    // console.log('formData', formData);
    // Change data to number
    const yearsRemainingLeaseNum = Number(yearsRemainingLease);
    const currentRentNum = Number(currentRent);
    // console.log('currentRentNum', currentRentNum);

    let capRateResult;
    let deferRateResult;
    if (yearsRemainingLease < 80) {
      capRateResult = 0.06;
      deferRateResult = 0.0517;
    } else {
      capRateResult = 0.07;
      deferRateResult = 0.05;
    }
    // console.log('capRateResult', capRateResult);
    // console.log('deferRateResult', deferRateResult);

    // FreeHold value demo 404,040 - C7
    const freeHoldValueCalc = Math.round(propertyValue / 0.99);
    const freeHoldValueResult = freeHoldValueCalc.toLocaleString();
    // console.log('freeHoldValueResult - C7', freeHoldValueResult);

    // FreeHolder Interest Rate demo 14.25 - E13
    const groundRentPercentage = capRateResult;
    const freeHolderInterestCalc =
      (1 - 1 / Math.pow(1 + groundRentPercentage, yearsRemainingLeaseNum)) / groundRentPercentage;
    const freeHolderInterestRateResult = freeHolderInterestCalc.toFixed(2);
    // console.log('freeHolderInterestRateResult - E13', freeHolderInterestRateResult);

    // Reversion to FHVP rate demo 0.0143 - E16
    const reversionCalc = Math.pow(1 / (1 + deferRateResult), yearsRemainingLeaseNum);
    const reversionResult = reversionCalc.toFixed(4);
    // console.log('reversionResult - E16', reversionResult);

    // FreeHolder Interest Rate Total demo £2,849.21 - F13
    const freeHolderAmount = freeHolderInterestRateResult * currentRentNum;
    const freeHolderAmountFormat = freeHolderAmount;
    const freeHolderAmountResultVal = Number(freeHolderAmountFormat);
    const freeHolderAmountResult = freeHolderAmountResultVal - reversionResult;

    // console.log('freeHolderAmountResult - F13', freeHolderAmountResult);

    // Reversion to FHVP total demo £5,794 - F16
    const getFreeHoldValueResultNumeric = parseInt(freeHoldValueResult.replace(/,/g, ''), 10);
    // console.log('getFreeHoldValueResultNumeric', getFreeHoldValueResultNumeric);
    const reversionAmountCalc = getFreeHoldValueResultNumeric * reversionResult;
    // console.log('reversionAmountCalc', reversionAmountCalc);
    const reversionAmountResult = Math.ceil(reversionAmountCalc);
    // console.log('reversionAmountResult - F16', reversionAmountResult); // 5778 not 5794

    // PV Year demo 177 - B21
    const pvYearCalc = yearsRemainingLeaseNum + 90;
    // console.log('pvYearCalc', pvYearCalc);

    // Round the result to 0.0002 adjust to 0.00017762 - E21
    const reversionProposedCalc = (1 / Math.pow(1 + deferRateResult, pvYearCalc)) * 1;
    const reversionProposedResult = reversionProposedCalc.toFixed(8);
    // console.log('reversionProposedResult - B21', reversionProposedResult);

    // Freeholder's proposed Interest Defer Total demo £72 - G21
    const freeholderProposedInterestDeferCalc = getFreeHoldValueResultNumeric * reversionProposedResult;
    const freeholderProposedInterestDeferResult = Math.round(freeholderProposedInterestDeferCalc);
    // console.log('freeholderProposedInterestDeferResult - G21', freeholderProposedInterestDeferResult);

    // freeHolderAmount + reversionAmountResult - G16 8628 instead of 8643
    const freeHolderAmountPlusReversionAmountResult = freeHolderAmountResult + reversionAmountResult;
    // console.log('freeHolderAmountPlusReversionAmountResult - G16', freeHolderAmountPlusReversionAmountResult);

    // Diminution in value of Freehold interest total demo £2,777 - G23
    const diminutionInValueCalc = freeHolderAmountPlusReversionAmountResult - freeholderProposedInterestDeferResult;
    // console.log('diminutionInValueCalc - G23', diminutionInValueCalc);

    // Premium Payable //

    // Define arrays for YEARS and RELATIVITY %
    const YearsRelativityVal = [
      { 39: 61.0 },
      { 40: 62.0 },
      { 41: 63.0 },
      { 42: 63.9 },
      { 43: 64.7 },
      { 44: 65.6 },
      { 45: 66.5 },
      { 46: 67.3 },
      { 47: 68.2 },
      { 48: 69.0 },
      { 49: 69.8 },
      { 50: 70.6 },
      { 51: 71.5 },
      { 52: 72.3 },
      { 53: 73.0 },
      { 54: 73.8 },
      { 55: 74.5 },
      { 56: 75.3 },
      { 57: 76.1 },
      { 58: 76.8 },
      { 59: 77.5 },
      { 60: 78.2 },
      { 61: 78.9 },
      { 62: 79.6 },
      { 63: 80.3 },
      { 64: 80.9 },
      { 65: 81.6 },
      { 66: 82.2 },
      { 67: 82.8 },
      { 68: 83.4 },
      { 69: 84.0 },
      { 70: 84.6 },
      { 71: 85.2 },
      { 72: 85.8 },
      { 73: 86.4 },
      { 74: 86.9 },
      { 75: 87.4 },
      { 76: 88.0 },
      { 77: 88.4 },
      { 78: 88.9 },
      { 79: 89.4 },
      { 80: 89.9 },
      { 81: 90.9 },
      { 82: 91.2 },
      { 83: 91.6 },
      { 84: 91.9 },
      { 85: 92.2 },
      { 86: 92.6 },
      { 87: 92.9 },
      { 88: 93.2 },
      { 89: 93.5 },
      { 90: 93.7 },
      { 91: 94.0 },
      { 92: 94.3 },
      { 93: 94.6 },
      { 94: 94.8 },
      { 95: 95.1 },
      { 96: 95.3 },
      { 97: 95.6 },
      { 98: 95.8 },
      { 99: 96.0 },
      { 100: 96.3 },
    ];

    function getRelativity(year) {
      const foundItem = YearsRelativityVal.find((item) => year in item);
      return foundItem ? foundItem[year] : null;
    }

    const relativityValue = getRelativity(yearsRemainingLeaseNum).toFixed(2);
    // console.log('relativityValue - B32', relativityValue);

    // Leaseholder's present Interest demo £375,354
    const leaseholderPresentInterestCalc = relativityValue * getFreeHoldValueResultNumeric;
    const roundedleaseholderPresentInterestCalc = Math.round(leaseholderPresentInterestCalc);
    const leaseholderPresentInterestTotal = roundedleaseholderPresentInterestCalc / 100;
    const leaseholderPresentInterestFormat = Number(leaseholderPresentInterestTotal);
    const leaseholderPresentInterestResult = Math.ceil(leaseholderPresentInterestFormat);
    // console.log('leaseholderPresentInterestResult - E31', leaseholderPresentInterestResult);

    // Leaseholder's present Interest Add FreeHold Proposed Interest demo £404,112
    const leaseholderPresentInterestAddFreeHoldProposedInterestCalc =
      getFreeHoldValueResultNumeric + freeholderProposedInterestDeferResult;
    const leaseholderPresentInterestAddFreeHoldProposedInterestResult = Number(
      leaseholderPresentInterestAddFreeHoldProposedInterestCalc
    );
    // console.log(
    //   'leaseholderPresentInterestAddFreeHoldProposedInterestResult - F28',
    //   leaseholderPresentInterestAddFreeHoldProposedInterestResult
    // );

    // Leaseholder's present Interest Add FreeHold Present Interest demo £378,203
    const leaseholderPresentInterestAddFreeHoldPresentInterestCalc =
      leaseholderPresentInterestResult + freeHolderAmountPlusReversionAmountResult;
    // console.log(
    //   'leaseholderPresentInterestAddFreeHoldPresentInterestCalc - F31',
    //   leaseholderPresentInterestAddFreeHoldPresentInterestCalc
    // );

    // Total mariage value demo £25,910
    const totalMarriageValueCalc =
      leaseholderPresentInterestAddFreeHoldProposedInterestResult -
      leaseholderPresentInterestAddFreeHoldPresentInterestCalc;
    const totalMarriageValueResult = Number(totalMarriageValueCalc);
    // console.log('totalMarriageValueResult - F33', totalMarriageValueResult);

    // 50% of total marriage value demo £0
    const threshold = 79 + 364 / 365;
    const fiftyPercentOfTotalMarriageValueCalc =
      yearsRemainingLeaseNum > threshold ? 0 : Math.round(totalMarriageValueResult / 2);
    const fiftyPercentOfTotalMarriageValueResult = Number(fiftyPercentOfTotalMarriageValueCalc);
    // console.log('fiftyPercentOfTotalMarriageValueResult - G35', fiftyPercentOfTotalMarriageValueResult);

    // Premium Payable demo £2,777
    const premiumPayableCalc = diminutionInValueCalc + fiftyPercentOfTotalMarriageValueResult;
    const premiumPayableResult = premiumPayableCalc;
    // console.log('premiumPayableResult - G37', premiumPayableResult);

    // Rounding to the nearest multiple of 100
    const premiumPayableResultRound = Math.ceil(premiumPayableResult / 100) * 100; // Rounding to the nearest multiple of 100
    // console.log('premiumPayableResultRound', premiumPayableResultRound);
    // Check if value is minus, set it to 0
    const premiumPayableResultRoundFormat = premiumPayableResultRound === 0 ? 0 : premiumPayableResultRound;
    // console.log('premiumPayableResultRoundFormat', premiumPayableResultRoundFormat);
    const premiumPayableResultFinal = premiumPayableResultRoundFormat.toLocaleString(2);

    // console.log('premiumPayableResultFinal', premiumPayableResultFinal);
    // Set the result values

    setEstimateLeaseCost(premiumPayableResultFinal);

    // Set the active state to the disclaimer

    setActiveView('disclaimer');
  };

  // console.log('estimateLeaseCost', estimateLeaseCost);

  // HAndle errors

  function formatErrorMessages(errorObjects) {
    if (typeof errorObjects !== 'object' || errorObjects === null) {
      return 'Invalid input, expecting an object with objects having errorMessage property.';
    }

    const errorMessages = Object.values(errorObjects)
      .filter((obj) => typeof obj === 'object' && obj !== null && obj.hasOwnProperty('message'))
      .map((obj) => obj.message);

    return errorMessages.join(', ');
  }

  const onError = (errorFields: FieldValues) => {
    const formattedErrors = formatErrorMessages(errorFields);

    // Data layer event

    dataLayerPush({
      event: 'leaseExtensionCalculator',
      lecFormStep: 'calculate lease extension',
      lecPropertyValue: watchPropertyValue ? watchPropertyValue.toString() : undefined,
      lecYearsRemainingOnLease: watchYearsRemainingLease ? watchYearsRemainingLease.toString() : undefined,
      lecCurrentRent: watchCurrentRent ? watchCurrentRent.toString() : undefined,
      formValidation: 'failure',
      formError: formattedErrors,
    });
  };
  const panelConfig = { ...PANEL_CONFIG, heading: heading || PANEL_CONFIG.heading };

  const generateId = (text?: string) => text?.toLowerCase().replace(/[^a-zA-Z0-9]/g, '-');

  return (
    <FormProvider {...formMethods}>
      <PanelGroup id={generateId(heading || PANEL_CONFIG.heading)}>
        {/* Calculator view */}

        {activeView === 'calculator' && (
          <Panel
            as="form"
            onSubmit={handleSubmit(onSubmit, onError)}
            method="post"
            noValidate
            buttonOne={{
              label: 'Calculate extension cost',
              variant: 'outline',
              type: 'submit',
              disableClickTracking: true,
            }}
            {...panelConfig}
            {...rest}
            border
            id="leaseExtensionCalculator"
            introCopy={introCopy}
            stretch
          >
            <StyledFormContainer spacing="half">
              {/* Property value */}

              <Input
                type="number"
                invalid={!!errors.propertyValue}
                valid={touchedFields.propertyValue && dirtyFields.propertyValue && !errors.propertyValue}
                errorMessage={errors.propertyValue?.message}
                label="Property value"
                id="propertyValue"
                placeholder="e.g 400000"
                {...register('propertyValue', {
                  // valueAsNumber: true,
                  required: `Enter your property value`,
                  pattern: {
                    value: /^[0-9]+$/,
                    message: `Enter a valid property value`,
                  },
                  validate: (value) => {
                    if (value < 10) {
                      return `Property value must be at least 10`;
                    }
                  },
                })}
                required
                prefix="£"
                hideAsterisk
                tooltipText="The value of the property after the lease extension."
                tooltipInfoIcon
              />

              <StyledInlineFields>
                {/* Years currently remaining on lease */}

                <Input
                  type="number"
                  label="Years remaining on lease"
                  placeholder="e.g 80"
                  id="yearsRemainingLease"
                  {...register('yearsRemainingLease', {
                    required: `Enter the years currently remaining on lease`,
                    pattern: {
                      value: /^[0-9]+$/,
                      message: `Enter a valid year`,
                    },
                    validate: (value) => {
                      if (value < 40) {
                        return `Value must be at least 40`;
                      }
                      if (value > 100) {
                        return `Value cannot exceed 100`;
                      }
                    },
                  })}
                  invalid={!!errors.yearsRemainingLease}
                  valid={dirtyFields.yearsRemainingLease && !errors.yearsRemainingLease}
                  errorMessage={errors.yearsRemainingLease?.message}
                  required
                  suffix="Yrs"
                  hideAsterisk
                />

                {/* Current ground rent */}

                <Input
                  type="number"
                  invalid={!!errors.currentRent}
                  valid={touchedFields.currentRent && dirtyFields.currentRent && !errors.currentRent}
                  errorMessage={errors.currentRent?.message}
                  label="Annual ground rent"
                  placeholder="e.g 200"
                  id="currentRent"
                  {...register('currentRent', {
                    required: `Enter your annual ground rent`,
                    valueAsNumber: true,
                    min: 0,
                  })}
                  required
                  prefix="£"
                  hideAsterisk
                />
              </StyledInlineFields>
            </StyledFormContainer>
          </Panel>
        )}

        {/* Disclaimer view */}

        {activeView === 'disclaimer' && (
          <Panel
            buttonOne={{
              label: 'Continue',
              variant: 'outline',
              type: 'button',
              customDataLayerEvent: {
                event: 'leaseExtensionCalculator',
                lecFormStep: 'continue',
                lecPropertyValue: watchPropertyValue,
                lecYearsRemainingOnLease: watchYearsRemainingLease,
                lecCurrentRent: watchCurrentRent.toString(),
                formValidation: 'success',
                formError: undefined,
              },
              onClick: () => {
                setActiveView('results');
              },
            }}
            {...panelConfig}
            {...rest}
            border
            stretch
          >
            <Spacer spacing="half">
              <Typography component="h4" variant="titleSmall">
                Important note
              </Typography>
              <Typography component="p" variant="bodyLarge">
                {/* This calculator is to be used as a guide only and is not a substitute for a professional valuation. It
                does not factor in any ground rent reviews, legal or surveyor fees. */}
                This calculator is intended as a guide only. The actual cost of extending your lease may be more or less
                than the figure provided by this calculator. For more information or to request a quote, please call 020
                3883 4776
              </Typography>
            </Spacer>
          </Panel>
        )}

        {/* Results view */}

        {activeView === 'results' && (
          <Panel
            buttonOne={{
              label: 'Calculate again',
              variant: 'outline',
              type: 'button',
              customDataLayerEvent: {
                event: 'leaseExtensionCalculator',
                lecFormStep: 'calculate again ',
                lecPropertyValue: watchPropertyValue,
                lecYearsRemainingOnLease: watchYearsRemainingLease,
                lecCurrentRent: watchCurrentRent.toString(),
                formValidation: 'success',
                formError: undefined,
              },
              onClick: () => {
                setActiveView('calculator');
              },
            }}
            {...panelConfig}
            {...rest}
            border
            stretch
          >
            <Spacer spacing="half">
              <div>
                <Typography component="h4" variant="titleSmall">
                  Estimated lease extension cost
                </Typography>
                <Typography component="dd" variant="bodyLarge">
                  £{estimateLeaseCost}
                </Typography>
              </div>

              <Typography component="h4" variant="titleSmall">
                Next step
              </Typography>
              <Typography component="p" variant="bodySmall">
                Our lease extension service is managed and coordinated by our dedicated in-house support team, who will
                provide an estimate for extending your lease and ensure you are placed with a specialist solicitor,
                surveyor and mortgage consultant to suit your needs.
              </Typography>
              <Typography component="p" variant="bodyLarge">
                <Button variant="link" onClick={handleOpenBlade}>
                  Get in touch
                </Button>
              </Typography>
              <LeaseExtensionQuote />
            </Spacer>
          </Panel>
        )}
      </PanelGroup>
    </FormProvider>
  );
};
